import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { useChatContext } from '../stores/ChatContext';
import { usePreferences } from '../stores/PreferencesContext';
import { useModels } from '../hooks/useModels';
import { useAuth } from '../auth/useAuth';
import ModelSelector from '../components/chat/ModelSelector';
import DeepThinkingToggle from '../components/chat/DeepThinkingToggle';
import ChatWindow from '../components/chat/ChatWindow';
import { Spinner } from '../components/common/Spinner';
import {
  createSession,
  getSession,
  streamMessage,
  type StreamCallbacks,
} from '../services/chatApi';
import { uploadFiles } from '../services/attachmentApi';
import AttachmentChip from '../components/chat/AttachmentChip';
import type { ChatSession, ChatMessage, UploadedAttachment } from '@mem-ai-hub/shared';
import { ALLOWED_EXTENSIONS, MAX_FILE_SIZE } from '@mem-ai-hub/shared';

interface OutletContext {
  refreshSessions: () => Promise<void>;
}

const SUGGESTIONS = [
  {
    icon: (
      <svg className="h-5 w-5 lg:h-6 lg:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
      </svg>
    ),
    title: 'Draft a Message',
    description: 'Emails, memos, or reports',
    prompt: 'Help me draft a professional email about ',
  },
  {
    icon: (
      <svg className="h-5 w-5 lg:h-6 lg:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
      </svg>
    ),
    title: 'Brainstorm Ideas',
    description: 'Creative solutions and approaches',
    prompt: 'Help me brainstorm ideas for ',
  },
  {
    icon: (
      <svg className="h-5 w-5 lg:h-6 lg:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
    ),
    title: 'Summarize Content',
    description: 'Key points from documents',
    prompt: 'Please summarize the following: ',
  },
];

export function ChatPage() {
  const { sessionId } = useParams<{ sessionId?: string }>();
  const navigate = useNavigate();
  const { setActiveSessionId } = useChatContext();
  const { refreshSessions } = useOutletContext<OutletContext>();
  const { user } = useAuth();
  const { models, isLoading: modelsLoading } = useModels();
  const { preferences } = usePreferences();

  const [session, setSession] = useState<ChatSession | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [deepThinkingEnabled, setDeepThinkingEnabled] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [thinkingContent, setThinkingContent] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sendAnimating, setSendAnimating] = useState(false);
  const [uploadedAttachments, setUploadedAttachments] = useState<UploadedAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(
    () => localStorage.getItem('april-2026-models-banner-dismissed') === '1',
  );

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<(() => void) | null>(null);
  const mountedRef = useRef(true);

  // Set page title
  useEffect(() => { document.title = 'AI Hub - Chat'; }, []);

  // Sync active session ID with URL
  useEffect(() => {
    setActiveSessionId(sessionId ?? null);
  }, [sessionId, setActiveSessionId]);

  // Load session when URL has sessionId
  useEffect(() => {
    if (!sessionId) {
      setSession(null);
      setMessages([]);
      return;
    }

    let cancelled = false;

    async function load() {
      setIsLoadingSession(true);
      try {
        const data = await getSession(sessionId!);
        if (!cancelled) {
          setSession(data.session);
          setMessages(data.messages);
        }
      } catch (err) {
        console.error('Failed to load session:', err);
        if (!cancelled) setError('Failed to load chat session.');
      } finally {
        if (!cancelled) setIsLoadingSession(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // Auto-select default model: use preference if set, else tier-based fallback
  useEffect(() => {
    if (!selectedModelId && models.length > 0) {
      // Prefer user's configured default model
      if (preferences.defaultModelId) {
        const defaultModel = models.find((m) => m.id === preferences.defaultModelId && m.enabled);
        if (defaultModel) {
          setSelectedModelId(defaultModel.id);
          return;
        }
      }
      // Fallback: prefer Claude Sonnet 4.6, then first available model
      const preferred = models.find((m) => m.id === 'claude-sonnet-4-6');
      setSelectedModelId((preferred ?? models[0]).id);
    }
  }, [models, selectedModelId, user, preferences.defaultModelId]);

  // Clean up abort and mark unmounted
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      abortRef.current?.();
    };
  }, []);

  // Auto-resize textarea
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  };

  // Handle file selection and upload
  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;

    // Reset the input so the same file can be re-selected
    e.target.value = '';

    // Client-side size validation
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        setError(`"${file.name}" exceeds the 20 MB file size limit.`);
        return;
      }
    }

    // Limit total attachments to 5
    if (uploadedAttachments.length + files.length > 5) {
      setError('You can attach up to 5 files per message.');
      return;
    }

    setIsUploading(true);
    setError(null);
    try {
      const result = await uploadFiles(files);
      setUploadedAttachments((prev) => [...prev, ...result.attachments]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'File upload failed');
    } finally {
      setIsUploading(false);
    }
  }, [uploadedAttachments.length]);

  const removeAttachment = useCallback((id: string) => {
    setUploadedAttachments((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const dismissBanner = useCallback(() => {
    setBannerDismissed(true);
    localStorage.setItem('april-2026-models-banner-dismissed', '1');
  }, []);

  // Determine deep thinking availability
  const selectedModel = useMemo(
    () => models.find((m) => m.id === (session?.modelId ?? selectedModelId)),
    [models, session, selectedModelId],
  );
  const canToggleDeepThinking = !!(
    user?.canDeepThink && selectedModel?.supportsDeepThinking
  );
  const deepThinkingVisible = !!user?.canDeepThink;

  const stopStream = useCallback(() => {
    abortRef.current?.();
    abortRef.current = null;
    setIsStreaming(false);
  }, []);

  // Send message handler
  const handleSend = useCallback(async () => {
    const content = inputValue.trim();
    const hasAttachments = uploadedAttachments.length > 0;
    if ((!content && !hasAttachments) || isStreaming || isSending) return;

    // Capture current attachments before clearing
    const currentAttachments = [...uploadedAttachments];
    const attachmentIds = currentAttachments.map((a) => a.id);

    setIsSending(true);
    setError(null);
    setInputValue('');
    setUploadedAttachments([]);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    let targetSessionId = session?.id ?? sessionId;

    try {
      // If no session, create one first
      if (!targetSessionId) {
        if (!selectedModelId) {
          setError('Please select a model first.');
          setInputValue(content);
          setIsSending(false);
          return;
        }
        const newSession = await createSession(selectedModelId);
        setSession(newSession);
        targetSessionId = newSession.id;
        navigate(`/chat/${newSession.id}`, { replace: true });
      }

      // Add optimistic user message
      const displayContent = content || 'Please analyze the attached file(s).';
      const userMsg: ChatMessage = {
        id: `temp-${Date.now()}`,
        sessionId: targetSessionId,
        role: 'user',
        content: displayContent,
        timestamp: new Date().toISOString(),
        ...(currentAttachments.length > 0 ? {
          attachments: currentAttachments.map((a) => ({
            id: a.id,
            fileName: a.fileName,
            mimeType: a.mimeType,
            category: a.category,
            size: a.size,
          })),
        } : {}),
      };
      setMessages((prev) => [...prev, userMsg]);

      // Abort any existing stream before starting a new one
      if (abortRef.current) {
        abortRef.current();
        abortRef.current = null;
      }

      // Stream the response
      setIsStreaming(true);
      setIsSending(false);
      setStreamingContent('');
      setThinkingContent('');

      const sid = targetSessionId;

      const callbacks: StreamCallbacks = {
        onToken: (token) => setStreamingContent((prev) => prev + token),
        onThinking: (thinking) =>
          setThinkingContent((prev) => prev + thinking),
        onDone: async (_messageId: string, title?: string) => {
          if (!mountedRef.current) return;
          setIsStreaming(false);
          abortRef.current = null;
          try {
            const data = await getSession(sid);
            if (!mountedRef.current) return;
            // If a title was auto-generated, use it immediately
            // (handles race condition where Cosmos write may not be complete)
            if (title) {
              data.session.title = title;
            }
            setSession(data.session);
            setMessages(data.messages);
          } catch {
            // Keep optimistic state if reload fails
          }
          refreshSessions();
        },
        onError: (err) => {
          if (!mountedRef.current) return;
          setIsStreaming(false);
          abortRef.current = null;
          setError(err);
        },
      };

      const abort = streamMessage(
        sid,
        content || '',
        deepThinkingEnabled,
        callbacks,
        attachmentIds.length > 0 ? attachmentIds : undefined,
      );
      abortRef.current = abort;
    } catch (err) {
      setIsSending(false);
      setIsStreaming(false);
      console.error('Failed to send message:', err);
      setError(err instanceof Error ? err.message : 'Failed to send message');
      setInputValue(content || '');
    }
  }, [
    inputValue,
    isStreaming,
    isSending,
    session,
    sessionId,
    selectedModelId,
    deepThinkingEnabled,
    uploadedAttachments,
    navigate,
    refreshSessions,
  ]);

  // Handle Enter key — respects sendWithEnter preference
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (preferences.sendWithEnter) {
      // Enter sends, Shift+Enter for newline
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    } else {
      // Shift+Enter sends, Enter for newline
      if (e.key === 'Enter' && e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    }
  };

  // Handle suggestion card click
  const handleSuggestion = (prompt: string) => {
    setInputValue(prompt);
    // Focus the textarea so user can complete their prompt
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        // Place cursor at end
        textareaRef.current.selectionStart = prompt.length;
        textareaRef.current.selectionEnd = prompt.length;
        // Trigger auto-resize
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
      }
    }, 0);
  };

  // Model selector disabled when in an active session or streaming
  const modelSelectorDisabled = !!session || isStreaming;

  const sendHint = preferences.sendWithEnter
    ? 'Shift+Enter for new line'
    : 'Shift+Enter to send';

  const firstName = user?.displayName?.split(' ')[0] ?? 'there';

  // Loading state for existing session
  if (isLoadingSession) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Top bar: model selector + deep thinking toggle */}
      <div className="flex items-start gap-3 pb-2">
        <div className="w-56 shrink-0">
          <ModelSelector
            models={models}
            selectedModelId={session?.modelId ?? selectedModelId}
            onSelect={setSelectedModelId}
            disabled={modelSelectorDisabled || modelsLoading}
            hiddenModelIds={preferences.hiddenModelIds}
            showDescriptions={preferences.showModelDescriptions}
          />
        </div>
        {/* pt aligns toggle with the select input (below the "Model" label) */}
        <div className="pt-6">
          <DeepThinkingToggle
            enabled={deepThinkingEnabled}
            onChange={setDeepThinkingEnabled}
            visible={deepThinkingVisible}
            disabled={!canToggleDeepThinking}
            disabledReason={
              !user?.canDeepThink
                ? "You don't have access to deep thinking"
                : !selectedModel?.supportsDeepThinking
                  ? "This model doesn't support deep thinking"
                  : undefined
            }
          />
        </div>
        {session && (
          <h3 className="ml-auto truncate pt-6 font-heading text-sm font-medium text-text-secondary">
            {session.title}
          </h3>
        )}
      </div>

      {/* Message area OR Welcome screen */}
      {!session && messages.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center px-4">
          {/* Logo with glow */}
          <div className="animate-fade-in-up mb-4 lg:mb-6">
            <div className="animate-pulse-glow mx-auto rounded-2xl p-1">
              <img
                src="/logo.png"
                alt="AI Hub"
                className="h-14 w-14 lg:h-20 lg:w-20 xl:h-24 xl:w-24 rounded-xl object-contain"
              />
            </div>
          </div>

          {/* Greeting */}
          <div className="animate-fade-in-up animate-delay-1 mb-1 text-center">
            <h2 className="font-heading text-2xl lg:text-3xl xl:text-4xl font-bold tracking-tight text-text-primary">
              Welcome, {firstName}
            </h2>
          </div>
          <p className="animate-fade-in-up animate-delay-2 mb-6 lg:mb-8 xl:mb-10 font-body text-sm lg:text-base xl:text-lg text-text-muted">
            What would you like to explore today?
          </p>

          {/* Model update banner */}
          {!bannerDismissed && (
            <div className="animate-fade-in-up animate-delay-2 mb-6 w-full max-w-xl lg:max-w-2xl xl:max-w-3xl rounded-xl border border-primary/20 bg-primary/5 px-4 py-3">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1 font-body text-sm text-text-secondary">
                  <span className="font-semibold text-text-primary">New: Claude Opus 4.7 and ChatGPT 5.4 Mini are now available!</span>{' '}
                  Anthropic's latest flagship model and a new cost-effective GPT-5.4 variant join the lineup.{' '}
                  <a
                    href="https://www.anthropic.com/news/claude-opus-4-7"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-primary underline decoration-primary/30 hover:decoration-primary"
                  >
                    Learn more
                  </a>
                </div>
                <button
                  type="button"
                  onClick={dismissBanner}
                  className="mt-0.5 shrink-0 text-text-muted transition-colors hover:text-text-primary"
                  aria-label="Dismiss banner"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Suggestion cards — 3 columns */}
          <div className="grid w-full max-w-xl lg:max-w-2xl xl:max-w-3xl grid-cols-3 gap-2.5 lg:gap-4 xl:gap-5">
            {SUGGESTIONS.map((suggestion, i) => (
              <button
                key={suggestion.title}
                type="button"
                onClick={() => handleSuggestion(suggestion.prompt)}
                className={`glass-subtle group rounded-xl p-3 lg:p-4 xl:p-5 text-left transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/5 animate-fade-in-up animate-delay-${i + 1}`}
              >
                <div className="mb-2 flex h-8 w-8 lg:h-10 lg:w-10 xl:h-12 xl:w-12 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary/20">
                  {suggestion.icon}
                </div>
                <h3 className="font-heading text-xs lg:text-sm xl:text-base font-semibold text-text-primary">
                  {suggestion.title}
                </h3>
                <p className="mt-0.5 font-body text-[11px] lg:text-xs xl:text-sm leading-tight text-text-muted">
                  {suggestion.description}
                </p>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <ChatWindow
          messages={messages}
          isStreaming={isStreaming}
          streamingContent={streamingContent}
          thinkingContent={thinkingContent}
          compact={preferences.compactMessageView}
        />
      )}

      {/* Input area — pinned to bottom, separated from chat stream */}
      <div className="shrink-0 border-t border-border/50 pt-2">
        {/* Error display */}
        {error && (
          <div role="alert" className="mx-2 mb-2 rounded-xl bg-error/10 px-4 py-2.5 text-sm text-error">
            {error}
            <button
              onClick={() => setError(null)}
              className="ml-2 text-error/70 hover:text-error"
            >
              Dismiss
            </button>
          </div>
        )}
        <div className="glass-subtle rounded-2xl p-2">
          {/* Attachment chips */}
          {(uploadedAttachments.length > 0 || isUploading) && (
            <div className="flex flex-wrap items-center gap-1.5 px-2 pb-1.5 pt-1">
              {uploadedAttachments.map((att) => (
                <AttachmentChip
                  key={att.id}
                  fileName={att.fileName}
                  size={att.size}
                  category={att.category}
                  onRemove={() => removeAttachment(att.id)}
                />
              ))}
              {isUploading && (
                <div className="inline-flex items-center gap-1.5 rounded-lg bg-bg-secondary border border-border-light px-2.5 py-1.5 text-xs text-text-muted">
                  <Spinner size="sm" />
                  <span>Uploading...</span>
                </div>
              )}
            </div>
          )}
          <div className="flex items-end gap-2">
            <div className="relative flex-1">
              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={`Message AI Hub... (${sendHint})`}
                disabled={isSending}
                rows={1}
                aria-label="Type your message"
                className="w-full resize-none rounded-xl border-0 bg-transparent py-3 pl-10 pr-4 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-0 disabled:opacity-50"
              />
              {/* Paperclip icon for file attachment */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted transition-colors hover:text-primary"
                title="Attach file"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                  />
                </svg>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                multiple
                accept={ALLOWED_EXTENSIONS.join(',')}
                onChange={handleFileChange}
              />
            </div>

            {/* Send / Stop button */}
            {isStreaming ? (
              <button
                onClick={stopStream}
                className="shrink-0 rounded-xl bg-error px-4 py-3 text-sm font-semibold text-white transition-all hover:bg-error/90"
              >
                Stop
              </button>
            ) : (
              <button
                onClick={() => {
                  if ((!inputValue.trim() && uploadedAttachments.length === 0) || isSending) return;
                  setSendAnimating(true);
                  handleSend();
                  // Reset animation state after animation completes
                  setTimeout(() => setSendAnimating(false), 400);
                }}
                disabled={(!inputValue.trim() && uploadedAttachments.length === 0) || isSending}
                className="shrink-0 overflow-hidden rounded-xl bg-gradient-to-r from-primary to-sky-400 px-4 py-3 text-sm font-semibold text-white shadow-md transition-all hover:shadow-lg hover:shadow-primary/20 hover:-translate-y-0.5 active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0 disabled:hover:shadow-md"
              >
                {isSending ? (
                  <Spinner size="sm" />
                ) : (
                  <svg
                    className={`h-5 w-5 transition-transform duration-400 ${sendAnimating ? 'send-takeoff' : ''}`}
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    {/* Paper airplane — filled, angled for flight */}
                    <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
                  </svg>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
